import { customerMarketShopfrontApi, Site } from '@envato/foundation-types'
import get from 'lodash-es/get'
import * as queryString from 'query-string'
import { match } from 'react-router-dom'
import { ThunkAction } from 'redux-thunk'

import { ILocationParams } from '../../common/reducers/reactRouterLocation'
import studioRecommendationsConfig from '../../config/sites/studio-recommendations'
import { getConfigBySite } from '../../config/sites'
import { AlertType } from '../../typings/alert'
import { IRecommendedSearches } from '../../typings/recommendedSearches'
import {
  ISearchHeadings,
  ISearchIngestedContent
} from '../../typings/seoMetadata'
import { IStudioRecommendations } from '../../typings/studioRecommendations'
import {
  noIndex,
  setCanonicalPath,
  setErrorPage,
  setSeoMeta,
  showAlert
} from '../actions/common'
import fetchRecommendedSearchesByCategoryResults from '../actions/search/fetchRecommendedSearchesByCategoryResults'
import fetchRecommendedSearchesByTermResults from '../actions/search/fetchRecommendedSearchesByTermResults'
import { reportSearchPageType } from '../analytics'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { searchFrontendFetchParams } from '../constants/searchQueryWhitelist'
import { IRootState } from '../reducers'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { buildCanonicalPathForSearchPage } from '../utils/buildCanonicalPath'
import {
  isCriticalError,
  isHttpError,
  isTimeoutError,
  logError,
  LogErrorLevel,
  ResponseDataError
} from '../utils/errors'
import { hasFeature } from '../utils/features'
import { decodeMetaString } from '../utils/formatString'
import ga from '../utils/ga'
import { getItemRankOffset } from '../utils/item'
import { getItemMatrix } from '../utils/itemMatrix'
import {
  getSearchLocationParams,
  getSearchTypeFromLocationParams
} from '../utils/location'
import makeSearchLink from '../utils/location/makeSearchLink'
import { sanitizeQueryParams } from '../utils/location/sanitize'
import { ICommonLocation, IFlatQuery } from '../utils/location/types'
import objectDiff from '../utils/objectDiff'
import {
  buildSearchQueryString,
  isCategoryOnlySearch
} from '../utils/searchQueryHelper'
import { getTopLevelRoute, makeLink } from '../utils/urlHelper'
import setStudioRecommendations from './search/setStudioRecommendations'

export enum TypeKeys {
  GET_SEARCH_RESULTS_REQUEST = 'GET_SEARCH_RESULTS_REQUEST',
  GET_SEARCH_RESULTS_SUCCESS = 'GET_SEARCH_RESULTS_SUCCESS',
  GET_SEARCH_RESULTS_FAIL = 'GET_SEARCH_RESULTS_FAIL',

  GET_INGESTED_CONTENT_SUCCESS = 'GET_INGESTED_CONTENT_SUCCESS',

  SET_SEARCH_HEADINGS = 'SET_SEARCH_HEADINGS',
  SET_ACTIVE_AUDIO = 'SET_ACTIVE_AUDIO',
  SET_STUDIO_RECOMMENDATIONS = 'SET_STUDIO_RECOMMENDATIONS',

  GET_ITEM_LICENCES_SUCCESS = 'GET_ITEM_LICENCES_SUCCESS',
  GET_ITEM_LICENCES_FAIL = 'GET_ITEM_LICENCES_FAIL',

  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST',
  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS',
  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL',

  GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS = 'GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS',
  GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL = 'GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL',

  GET_SIMILAR_ITEMS_SUCCESS = 'GET_SIMILAR_ITEMS_SUCCESS',
  GET_SIMILAR_ITEMS_FAIL = 'GET_SIMILAR_ITEMS_FAIL'
}

export interface IGetSearchResultsRequestAction {
  type: TypeKeys.GET_SEARCH_RESULTS_REQUEST
}

export interface IGetSearchResultsSuccessAction {
  type: TypeKeys.GET_SEARCH_RESULTS_SUCCESS
  payload: Readonly<{
    results: customerMarketShopfrontApi.search.IResult
  }>
}

export interface IGetSearchResultsFailAction {
  type: TypeKeys.GET_SEARCH_RESULTS_FAIL
  error: boolean
  httpStatus: number
  timeout: boolean
}

export interface IGetIngestedContentSuccessAction {
  type: TypeKeys.GET_INGESTED_CONTENT_SUCCESS
  payload: Readonly<{ content: ISearchIngestedContent }>
}

export interface ISetSearchHeadingsAction {
  type: TypeKeys.SET_SEARCH_HEADINGS
  payload: Readonly<ISearchHeadings>
}

export interface ISetActiveAudioAction {
  type: TypeKeys.SET_ACTIVE_AUDIO
  payload: Readonly<{ activeAudio: string }>
}

export interface IGetItemLicencesSuccessAction {
  type: TypeKeys.GET_ITEM_LICENCES_SUCCESS
  payload: Readonly<{ itemID: number; licenses: any }>
}

export interface IGetItemLicencesFailAction {
  type: TypeKeys.GET_ITEM_LICENCES_FAIL
  payload: Readonly<{ itemID: number; error: any }>
}

export interface IGetRecommendedSearchesByTermResultsRequestAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST
}

export interface IGetRecommendedSearchesByTermResultsSuccessAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS
  payload: Readonly<IRecommendedSearches>
}

export interface IGetRecommendedSearchesByTermResultsFailAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL
  error: boolean
}

export interface IGetRecommendedSearchesByCategoryResultsSuccessAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS
  payload: Readonly<IRecommendedSearches>
}

export interface IGetRecommendedSearchesByCategoryResultsFailAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL
}

export interface ISetStudioRecommendationsAction {
  type: TypeKeys.SET_STUDIO_RECOMMENDATIONS
  payload: Readonly<IStudioRecommendations>
}

export interface IGetSimilarItemsSuccessAction {
  type: TypeKeys.GET_SIMILAR_ITEMS_SUCCESS
  payload: Readonly<{ itemID: number; similarItems: any }>
}

export interface IGetSimilarItemsFailAction {
  type: TypeKeys.GET_SIMILAR_ITEMS_FAIL
  payload: Readonly<{ itemID: number; error: string }>
}

export type ActionTypes =
  | IGetSearchResultsRequestAction
  | IGetSearchResultsSuccessAction
  | IGetSearchResultsFailAction
  | IGetIngestedContentSuccessAction
  | ISetSearchHeadingsAction
  | ISetActiveAudioAction
  | IGetItemLicencesSuccessAction
  | IGetItemLicencesFailAction
  | IGetRecommendedSearchesByTermResultsRequestAction
  | IGetRecommendedSearchesByTermResultsSuccessAction
  | IGetRecommendedSearchesByTermResultsFailAction
  | IGetRecommendedSearchesByCategoryResultsSuccessAction
  | IGetRecommendedSearchesByCategoryResultsFailAction
  | ISetStudioRecommendationsAction
  | IGetSimilarItemsSuccessAction
  | IGetSimilarItemsFailAction

interface INoIndexParam {
  name: string
  value?: string
}

const shouldFetchData = (
  previousLocation: ICommonLocation | null,
  currentLocation: ICommonLocation | null
) => {
  if (!previousLocation || !currentLocation) {
    return true
  }

  const isPathnameDifferent =
    previousLocation.pathname !== currentLocation.pathname

  const isQueryDifferent = objectDiff(
    previousLocation.query,
    currentLocation.query
  ).some(key => searchFrontendFetchParams.includes(key))

  return isPathnameDifferent || isQueryDifferent
}

const hasNoIndexParams = (
  noIndexParams: INoIndexParam[],
  locationParams: IFlatQuery
) => {
  return noIndexParams.some(param => {
    const { name, value } = param
    const locationParam = locationParams[name]

    return !!locationParam && (value ? locationParam == value : true)
  })
}

const isNoIndexRoutes = (noIndexRoutes: string[], routeMatch: match<any>) => {
  return noIndexRoutes.includes(routeMatch.path)
}

export const onBeforeSearchPageView = ({
  location,
  routeMatch,
  prevLocation
}: {
  location: ICommonLocation
  routeMatch: match<any>
  prevLocation?: ICommonLocation
}): ThunkAction<void, IRootState, void> => (dispatch, getState) => {
  const experiments = getState().experiments
  const currentSite = getState().common.currentSite

  const newRoutesEnabled = hasFeature(
    'routes:new',
    currentSite,
    location,
    experiments
  )

  const locationParams = getSearchLocationParams({
    location,
    routeMatch,
    currentSite
  })

  performReportProductImpressions(
    currentSite,
    locationParams,
    getState,
    newRoutesEnabled
  )

  reportSearchPageType(locationParams, currentSite)

  if (newRoutesEnabled) {
    const url = `${window.location.origin}/search?${queryString.stringify(
      sanitizeQueryParams(locationParams)
    )}`
    ga('set', 'location', url)
  }
}

const performReportProductImpressions = (
  currentSite: Site,
  locationParams: ILocationParams,
  getState: () => IRootState,
  newRoutesEnabled: boolean
) => {
  const currentLocation = getState().router.location
  const currentPage = Number(currentLocation.query.page) || 0
  const itemRankOffset = getItemRankOffset(currentSite, currentPage)
  const items = getState().searchPage.results.matches
  const topLevelRoute = newRoutesEnabled
    ? getSearchTypeFromLocationParams(locationParams)
    : getTopLevelRoute(currentLocation.pathname)

  reportProductsImpressions({
    items,
    currentSite,
    itemRankOffset,
    page: 'Search Results',
    section: topLevelRoute
  })
}

const fetchSearchResults = ({
  location,
  apiAppUrlPrefix,
  routeMatch
}: {
  location: ICommonLocation
  routeMatch: match<any>
  apiAppUrlPrefix: string
}): ThunkAction<Promise<any>, IRootState, void> => {
  return (dispatch, getState) => {
    const currentSite: Site =
      get(getState(), 'common.currentSite') || Site.themeforest
    const { previousLocation } = getState().router

    if (!shouldFetchData(previousLocation, location)) {
      return Promise.resolve()
    }

    const locationParams = getSearchLocationParams({
      location,
      routeMatch,
      currentSite
    })

    // Set no index when has certain search filters and route
    // https://trello.com/c/GrvQ6IRl/606-3-disallownoindex-compatiblewith-parameter-tf-seo
    const routes = getConfigBySite(currentSite, 'noIndex.routes')
    const params = getConfigBySite(currentSite, 'noIndex.params')

    const noIndexByRoute = routes && isNoIndexRoutes(routes, routeMatch)
    const noIndexByParam = params && hasNoIndexParams(params, locationParams)

    if (noIndexByRoute || noIndexByParam) {
      dispatch(noIndex())
    }

    const searchStringQuery = buildSearchQueryString(
      locationParams,
      currentSite
    )
    const hasItemId = locationParams.item_id
    const hasSearchTerm = locationParams.term

    dispatch(fetchSearchResultsRequest())

    const newRoutesEnabled = hasFeature('routes:new', currentSite, location)
    dispatch(
      setCanonicalPath(
        buildCanonicalPathForSearchPage(
          currentSite,
          locationParams,
          newRoutesEnabled ? makeSearchLink : makeLink
        )
      )
    )

    const requestsPromises = []

    if (hasSearchTerm && !hasItemId) {
      requestsPromises.push(
        dispatch(
          fetchRecommendedSearchesByTermResults(locationParams.term, {
            currentSite,
            apiAppUrlPrefix
          })
        )
      )
    }

    const searchUrl = hasItemId
      ? `${apiAppUrlPrefix}/shopfront-api/search/similar?${searchStringQuery}`
      : `${apiAppUrlPrefix}/shopfront-api/search?${searchStringQuery}`

    const axiosResponse = axiosVerbose
      .get<customerMarketShopfrontApi.search.IResult>(searchUrl)
      .then(res => {
        if (res.data && res.data.matches) {
          if (res.data.matches.length === 0) {
            dispatch(noIndex())
          }

          if (res.data.metadata) {
            if (typeof document !== 'undefined') {
              document.title = decodeMetaString(res.data.metadata.title)
            }
            dispatch(setSearchHeadings(res.data.metadata))
            dispatch(setSeoMeta(res.data.metadata))
          }

          dispatch(fetchSearchResultsSuccess(res.data))

          if (res.data.searchIngestedContent) {
            dispatch(
              fetchIngestedContentSuccess(res.data.searchIngestedContent)
            )
          }
          dispatch(
            setStudioRecommendations(
              studioRecommendationsConfig[currentSite] || {}
            )
          )

          // process other types of recommendations if term is not present
          if (isCategoryOnlySearch(locationParams)) {
            dispatch(
              fetchRecommendedSearchesByCategoryResults(
                locationParams,
                res.data,
                currentSite
              )
            )
          }
          return null
        } else {
          throw ResponseDataError({ response: res })
        }
      })
      .catch(err => {
        if (isCriticalError(err)) {
          return Promise.reject(
            logError(err, { level: LogErrorLevel.critical })
          )
        } else {
          return Promise.reject(logError(err))
        }
      })
      .catch(err => {
        return Promise.reject(dispatch(fetchSearchResultsFailure(err)))
      })

    requestsPromises.push(axiosResponse)

    return Promise.all(requestsPromises)
  }
}

/**
 * Search result actions
 */
const fetchSearchResultsRequest = () => {
  const action: IGetSearchResultsRequestAction = {
    type: TypeKeys.GET_SEARCH_RESULTS_REQUEST
  }
  return action
}
const fetchSearchResultsSuccess = (
  results: customerMarketShopfrontApi.search.IResult
): ThunkAction<IGetSearchResultsSuccessAction, IRootState, void> => {
  return (dispatch, getState) => {
    const action: IGetSearchResultsSuccessAction = {
      type: TypeKeys.GET_SEARCH_RESULTS_SUCCESS,
      payload: { results }
    }
    return dispatch(action)
  }
}
const fetchSearchResultsFailure = (
  error: any
): ThunkAction<IGetSearchResultsFailAction, IRootState, void> => {
  return dispatch => {
    let timeout = false

    if (!isHttpError(error, 499)) {
      dispatch(noIndex())
    }

    if (isCriticalError(error)) {
      dispatch(setErrorPage('SearchOffline'))
    } else {
      if (isTimeoutError(error)) {
        timeout = true
      }

      if (!isHttpError(error, 499)) {
        dispatch(showAlert(error.message, AlertType.failure, false))
      }
    }

    const action: IGetSearchResultsFailAction = {
      type: TypeKeys.GET_SEARCH_RESULTS_FAIL,
      error: true,
      httpStatus: 500,
      timeout
    }

    return dispatch(action)
  }
}

/**
 * IngestedContentSuccess
 */
const fetchIngestedContentSuccess = (content: ISearchIngestedContent) => {
  const action: IGetIngestedContentSuccessAction = {
    type: TypeKeys.GET_INGESTED_CONTENT_SUCCESS,
    payload: { content }
  }
  return action
}

/**
 * Search headings
 */
const setSearchHeadings = ({ heading, sub_heading }: ISearchHeadings) => {
  const action: ISetSearchHeadingsAction = {
    type: TypeKeys.SET_SEARCH_HEADINGS,
    payload: {
      heading,
      sub_heading
    }
  }
  return action
}

export const setActiveAudio = (activeAudio: string) => {
  const action: ISetActiveAudioAction = {
    type: TypeKeys.SET_ACTIVE_AUDIO,
    payload: { activeAudio }
  }
  return action
}

/**
 * Search item licenses
 */
export const fetchItemLicenses = (
  itemID: number
): ThunkAction<
  Promise<IGetItemLicencesSuccessAction | IGetItemLicencesFailAction>,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const licenses = get(getState(), 'searchPage.licenses')
    const errorOccured = !!(licenses[itemID] && licenses[itemID].error)

    if (!licenses[itemID] || errorOccured) {
      return getItemMatrix(itemID)
        .then(licensesData => {
          return dispatch(
            fetchItemLicensesSuccess({ itemID, licenses: licensesData })
          )
        })
        .catch(error => {
          return dispatch(fetchItemLicensesFailure(itemID, error.message))
        })
    } else {
      return Promise.resolve(
        dispatch(
          fetchItemLicensesSuccess({ itemID, licenses: licenses[itemID] })
        )
      )
    }
  }
}

const fetchItemLicensesSuccess = ({
  itemID,
  licenses
}: {
  itemID: number
  licenses: any
}) => {
  const action: IGetItemLicencesSuccessAction = {
    type: TypeKeys.GET_ITEM_LICENCES_SUCCESS,
    payload: { itemID, licenses }
  }
  return action
}

const fetchItemLicensesFailure = (itemID: number, error: any) => {
  const action: IGetItemLicencesFailAction = {
    type: TypeKeys.GET_ITEM_LICENCES_FAIL,
    payload: { itemID, error }
  }
  return action
}

export default fetchSearchResults
